import React, { useMemo } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import CareGapsLightIcon from "@/assets/svg/careGapsLightIcon.svg";
import CareGapsDarkIcon from "@/assets/svg/careGapsDarkIcon.svg";

import { DataTable } from "@/components/common/Table";
import { columns } from "./careGapsColumn";
import { routes } from "@/utils/urls";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { CareGapsSchema, CareGapsResponse } from "./careGaps";
import { Typography } from "@/components/ui/typography";
import { formatDate } from "@/constants/utilityFunctions";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface CareGapsProps {
  shouldBlink?: boolean;
}

const CareGaps: React.FC<CareGapsProps> = ({ shouldBlink = false }) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("CARE_GAPS");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const { data: careGapsResponse, loading, error, isErrorRed } = useFetchCard<CareGapsSchema[]>({
    endpoint: routes.cards.careGaps,
    memberId,
    processData: (rawData: unknown) => (rawData as CareGapsResponse).data,
  });

  const careGapsData = useMemo(() => {
    if (!careGapsResponse) return [];
    return careGapsResponse.map((item, index) => ({
      id: index + 1,
      claimsDate: item.claimsDate,
      metricShort: item.metricShort,
      metricDescription: item.metricDescription,
      metricStatus: item.metricStatus,
      loadDt: item.loadDt,
    }));
  }, [careGapsResponse]);

  const lastUpdatedText = (() => {
    if (loading) return "Loading...";
    if (error) return "-";
    return formatDate(careGapsData[0]?.loadDt || "-");
  })();

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <img
                src={CareGapsLightIcon}
                alt='CareGapsLightIcon'
                className='w-4 h-4 block dark:hidden'
              />
              <img
                src={CareGapsDarkIcon}
                alt='CareGapsDarkIcon'
                className='w-4 h-4 hidden dark:block'
              />
            </BorderedIconWrapper>
          }
          className='h-[56px]'
          title={
            <div className='flex flex-col h-[44px] gap-0'>
              <Typography
                variant='semibold-16'
                className='text-grey-800 dark:text-white'>
                Care Gaps
              </Typography>
              <Typography
                className='text-grey-600 dark:text-grey-500 text-right'
                style={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: "12px",
                  lineHeight: "20px",
                  letterSpacing: "0px",
                }}>
                Last updated on{" "}
                {lastUpdatedText}
              </Typography>
            </div>
          }
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 lg:h-[312px] md:h-[312px] sm:h-full'>
          {error ? (
            <div className='flex items-center justify-center sm:h-full lg:h-[312px] md:h-[312px]'>
                <Typography
                  variant='medium-14'
                  className={`${
                    isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                  } font-medium`}>
                  {error}
                </Typography>
            </div>
          ) : (
            <DataTable<CareGapsSchema>
              data={{
                data: careGapsData,
                total: careGapsData.length,
              }}
              columns={columns}
              height='auto'
              maxHeight={"280px"}
              isLoading={loading}
              skeletonCount={6}
            />
          )}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default CareGaps;
